import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useContext, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiCpu, FiTrash2 } from "react-icons/fi"

import { OpenAPI, TagTemplatesService, TagsService } from "@/client"
import type { TagCreate, TagUpdate } from "@/client"
import {
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
  VisionSelect,
} from "@/components/VisionUI"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/tag-form")({
  component: TagForm,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tagId: search.tagId as string | undefined,
    }
  },
})

interface FormData {
  name: string
  epcid: string
  url?: string
  tag_template_id?: string
  enabled: boolean
  // Asset tracking
  description?: string
  category?: string
  serial_number?: string
  sku?: string
  images?: string[]
  location_building?: string
  location_floor?: string
  location_zone?: string
  location_room?: string
  assigned_to?: string
  acquisition_date?: string
  asset_value?: number
  asset_status?: string
  notes?: string
}

function TagForm() {
  const { t } = useTranslation(["tags", "common"])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const sc = useSemanticColors()
  const { tagId } = useSearch({ from: "/_layout/tag-form" })
  const { currentOrganization } = useContext(OrganizationContext)
  const isEditMode = !!tagId

  // Image upload state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [existingImages, setExistingImages] = useState<string[]>([])

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return
    setPendingFiles((prev) => [...prev, ...files])
    // Reset input so same file can be selected again
    e.target.value = ""
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      enabled: true,
    },
  })

  const enabledValue = watch("enabled")
  const categoryValue = watch("category") ?? ""
  const skuValue = watch("sku") ?? ""

  // Sourced from the catalog tables (TagCategory / TagSkuCatalog) so
  // an operator can pre-create a value before any tag uses it. The
  // catalog is the single source of truth for the pulldowns; legacy
  // distinct-values endpoints are kept as a fallback for usage counts
  // on the dedicated admin pages but not consulted here.
  const distinctCategoriesQuery = useQuery({
    queryKey: ["tag-categories", currentOrganization?.id],
    queryFn: async (): Promise<string[]> => {
      if (!currentOrganization) return []
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const r = await fetch(
        `${base}/api/v1/organizations/${currentOrganization.id}/tag-categories/?limit=500`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!r.ok) return []
      const j = (await r.json()) as { data?: { name: string }[] }
      return (j.data ?? []).map((d) => d.name)
    },
    enabled: !!currentOrganization,
    staleTime: 60_000,
  })
  const distinctSkusQuery = useQuery({
    queryKey: ["tag-sku-catalog", currentOrganization?.id],
    queryFn: async (): Promise<string[]> => {
      if (!currentOrganization) return []
      const token = localStorage.getItem("access_token")
      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
      const r = await fetch(
        `${base}/api/v1/organizations/${currentOrganization.id}/tag-skus-catalog/?limit=500`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!r.ok) return []
      const j = (await r.json()) as { data?: { value: string }[] }
      return (j.data ?? []).map((d) => d.value)
    },
    enabled: !!currentOrganization,
    staleTime: 60_000,
  })
  const categoryOptions = distinctCategoriesQuery.data ?? []
  const skuOptions = distinctSkusQuery.data ?? []

  // True when the saved value isn't in the catalog (legacy free-typed
  // value), so we render the field in "type" mode and keep the value
  // visible instead of silently blanking it.
  const [categoryMode, setCategoryMode] = useState<"pick" | "type">("pick")
  const [skuMode, setSkuMode] = useState<"pick" | "type">("pick")
  useEffect(() => {
    if (categoryValue && !categoryOptions.includes(categoryValue)) {
      setCategoryMode("type")
    }
  }, [categoryValue, categoryOptions])
  useEffect(() => {
    if (skuValue && !skuOptions.includes(skuValue)) {
      setSkuMode("type")
    }
  }, [skuValue, skuOptions])

  // Fetch templates for dropdown
  const { data: templates = [] } = useQuery({
    queryKey: ["tag-templates", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return []
      try {
        const response = await TagTemplatesService.readOrganizationTemplates({
          organisationId: currentOrganization.id,
          skip: 0,
          limit: 100,
        })
        return response
      } catch (error) {
        return []
      }
    },
    enabled: !!currentOrganization,
  })

  // Fetch tag data if in edit mode
  const { data: tag, isLoading: tagLoading } = useQuery({
    queryKey: ["tag", tagId],
    queryFn: () => TagsService.readTag({ id: tagId! }),
    enabled: isEditMode,
  })

  // Reset form when tag data is loaded
  useEffect(() => {
    if (tag) {
      reset({
        name: tag.name,
        epcid: tag.epcid,
        url: tag.url || undefined,
        tag_template_id: tag.tag_template_id || undefined,
        enabled: tag.enabled,
        description: tag.description || undefined,
        category: tag.category || undefined,
        serial_number: tag.serial_number || undefined,
        sku: tag.sku || undefined,
        images: tag.images || undefined,
        location_building: tag.location_building || undefined,
        location_floor: tag.location_floor || undefined,
        location_zone: tag.location_zone || undefined,
        location_room: tag.location_room || undefined,
        assigned_to: tag.assigned_to || undefined,
        acquisition_date: tag.acquisition_date || undefined,
        asset_value: tag.asset_value ?? undefined,
        asset_status: tag.asset_status || undefined,
        notes: tag.notes || undefined,
      })
      // Load existing images
      if (tag.images?.length) {
        setExistingImages(tag.images)
      }
    }
  }, [tag, reset])

  const createTagMutation = useMutation({
    mutationFn: async (data: TagCreate) => {
      const result = await TagsService.createOrganizationTag({
        organisationId: currentOrganization!.id,
        requestBody: data,
      })
      return result
    },
    onSuccess: async (result: any) => {
      if (pendingFiles.length > 0 && result?.id) await uploadImages(result.id)
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      showToast.showSuccessToast(t("tags:form.tagCreated"))
      navigate({ to: "/tags" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to create tag"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const updateTagMutation = useMutation({
    mutationFn: async (data: TagUpdate) => {
      const orgId = tag?.organisation_id ?? currentOrganization!.id
      const result = await TagsService.updateOrganizationTag({
        organisationId: orgId,
        id: tagId!,
        requestBody: data,
      })
      return result
    },
    onSuccess: async () => {
      if (pendingFiles.length > 0 && tagId) await uploadImages(tagId)
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      queryClient.invalidateQueries({ queryKey: ["tag", tagId] })
      showToast.showSuccessToast(t("tags:form.tagUpdated"))
      navigate({ to: "/tags" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to update tag"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  // Permanent (hard) delete — bypasses the soft-delete trash. Requires
  // a one-click confirmation from a dialog rendered in the right
  // sidebar; the dialog is the only way to trigger this mutation.
  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false)
  const permanentDeleteMutation = useMutation({
    mutationFn: async () => {
      const orgId = tag?.organisation_id ?? currentOrganization!.id
      return TagsService.permanentlyDeleteOrganizationTag({
        organisationId: orgId,
        id: tagId!,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags"] })
      queryClient.invalidateQueries({ queryKey: ["tag", tagId] })
      showToast.showSuccessToast(t("tags:form.tagPermanentlyDeleted"))
      setPermanentDeleteOpen(false)
      navigate({ to: "/tags" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to delete tag"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const uploadImages = async (targetTagId: string) => {
    if (pendingFiles.length === 0) return
    const orgId = tag?.organisation_id ?? currentOrganization?.id
    if (!orgId) return
    const token = localStorage.getItem("access_token")
    const base = (OpenAPI.BASE || "").replace(/\/$/, "")
    for (const file of pendingFiles) {
      const formData = new FormData()
      formData.append("file", file)
      await fetch(
        `${base}/api/v1/organizations/${orgId}/tags/${targetTagId}/upload-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      )
    }
  }

  const handleFormSubmit = (data: FormData) => {
    if (!isEditMode && !currentOrganization) {
      showToast.showErrorToast(t("common:org.noOrg"))
      return
    }
    if (isEditMode && !tag?.organisation_id && !currentOrganization) {
      showToast.showErrorToast(t("common:org.noOrg"))
      return
    }

    const payload = {
      name: data.name,
      epcid: data.epcid,
      url: data.url ? data.url.trim() || undefined : undefined,
      enabled: data.enabled,
      tag_template_id: data.tag_template_id || undefined,
      description: data.description || undefined,
      category: data.category || undefined,
      serial_number: data.serial_number || undefined,
      sku: data.sku || undefined,
      location_building: data.location_building || undefined,
      location_floor: data.location_floor || undefined,
      location_zone: data.location_zone || undefined,
      location_room: data.location_room || undefined,
      assigned_to: data.assigned_to || undefined,
      acquisition_date: data.acquisition_date || undefined,
      asset_value: data.asset_value ?? undefined,
      asset_status: data.asset_status || undefined,
      notes: data.notes || undefined,
    }

    if (isEditMode) {
      updateTagMutation.mutate(payload)
    } else {
      const createPayload: TagCreate = {
        ...payload,
        organisation_id: currentOrganization!.id,
        tag_template_id: data.tag_template_id || "",
      }
      createTagMutation.mutate(createPayload)
    }
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (isEditMode && tagLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  return (
    <Box>
      <Flex align="center" gap={4} mb={8}>
        <Box flex={1}>
          <Heading size="xl" color={sc.heading}>
            {isEditMode ? t("tags:form.editTag") : t("tags:form.addTag")}
          </Heading>
          <Text color={sc.textMuted} mt={1}>
            {isEditMode ? t("tags:form.editTagSub") : t("tags:form.addTagSub")}
          </Text>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/tags" })}
          size="sm"
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Grid templateColumns={{ base: "1fr", lg: "3fr 1fr" }} gap={6}>
        {/* ══════ LEFT — FORM (75%) ══════ */}
        <Box>
          <VStack gap={6} as="form" onSubmit={handleSubmit(handleFormSubmit)}>
            <VisionCard w="100%">
              <Flex align="center" gap={3} mb={6}>
                <IconBox variant="glass" size="md">
                  <FiCpu fontSize="20px" color={sc.iconColor} />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("tags:form.tagConfig")}
                </Heading>
              </Flex>

              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
                w="100%"
              >
                <Field
                  required
                  invalid={!!errors.name}
                  errorText={errors.name?.message}
                >
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.tagName")}
                  </Text>
                  <VisionInput
                    {...register("name", {
                      required: t("tags:form.tagNameRequired"),
                      minLength: {
                        value: 2,
                        message: "Name must be at least 2 characters",
                      },
                    })}
                    placeholder={t("tags:form.tagNamePlaceholder")}
                    size="lg"
                    variant="glass"
                  />
                </Field>

                <Field
                  required
                  invalid={!!errors.epcid}
                  errorText={errors.epcid?.message}
                >
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.epcId")}
                  </Text>
                  <VisionInput
                    {...register("epcid", {
                      required: t("tags:form.epcIdRequired"),
                      pattern: {
                        value: /^[0-9A-Fa-f]+$/,
                        message: t("tags:form.epcIdInvalid"),
                      },
                    })}
                    placeholder={t("tags:form.epcIdPlaceholder")}
                    size="lg"
                    variant="glass"
                  />
                </Field>

                <Field
                  invalid={!!errors.url}
                  errorText={errors.url?.message}
                >
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.url", "Public DPP URL (optional)")}
                  </Text>
                  <VisionInput
                    {...register("url")}
                    placeholder={t(
                      "tags:form.urlPlaceholder",
                      "https://tapdpp.qdat.io/<EPCID-or-UID>",
                    )}
                    size="lg"
                    variant="glass"
                  />
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t(
                      "tags:form.urlHelp",
                      "The URL QDatDroid writes into NFC NDEF / UHF user memory. Per-org unique. Leave blank to use the default https://<dpp_host>/<EPCID> resolver path.",
                    )}
                  </Text>
                </Field>

                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.tagTemplate")}
                  </Text>
                  <VisionSelect {...register("tag_template_id")}>
                    <option value="">{t("tags:form.noTemplate")}</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.tag_type})
                      </option>
                    ))}
                  </VisionSelect>
                </Field>

                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.status")}
                  </Text>
                  <Checkbox
                    checked={enabledValue}
                    onCheckedChange={(checked) =>
                      setValue("enabled", checked.checked as boolean)
                    }
                    size="lg"
                  >
                    <Text color={sc.heading} fontSize="sm">
                      {t("tags:form.enabled")}
                    </Text>
                  </Checkbox>
                </Field>
              </Grid>
            </VisionCard>

            <VisionCard w="100%">
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.assetInfo")}
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.description")}
                  </Text>
                  <VisionInput
                    {...register("description")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.category")}
                  </Text>
                  {categoryMode === "pick" ? (
                    <VisionSelect
                      value={categoryValue}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "__create__") {
                          setCategoryMode("type")
                          setValue("category", "", { shouldDirty: true })
                          return
                        }
                        setValue("category", v, { shouldDirty: true })
                      }}
                    >
                      <option value="">— (none) —</option>
                      <option value="__create__">+ Create new category…</option>
                      {categoryOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </VisionSelect>
                  ) : (
                    <Flex gap={2} align="center">
                      <VisionInput
                        {...register("category")}
                        size="lg"
                        variant="glass"
                        placeholder="e.g. Apparel"
                      />
                      <VisionButton
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          setCategoryMode("pick")
                          setValue("category", "", { shouldDirty: true })
                        }}
                      >
                        ← Pick
                      </VisionButton>
                    </Flex>
                  )}
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.serialNumber")}
                  </Text>
                  <VisionInput
                    {...register("serial_number")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.sku")}
                  </Text>
                  {skuMode === "pick" ? (
                    <VisionSelect
                      value={skuValue}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === "__create__") {
                          setSkuMode("type")
                          setValue("sku", "", { shouldDirty: true })
                          return
                        }
                        setValue("sku", v, { shouldDirty: true })
                      }}
                    >
                      <option value="">— (none) —</option>
                      <option value="__create__">+ Create new SKU…</option>
                      {skuOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </VisionSelect>
                  ) : (
                    <Flex gap={2} align="center">
                      <VisionInput
                        {...register("sku")}
                        size="lg"
                        variant="glass"
                        placeholder="e.g. 00012345678905"
                      />
                      <VisionButton
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          setSkuMode("pick")
                          setValue("sku", "", { shouldDirty: true })
                        }}
                      >
                        ← Pick
                      </VisionButton>
                    </Flex>
                  )}
                </Field>
                <Field gridColumn={{ md: "span 2" }}>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.image")}
                  </Text>
                  <HStack gap={3} flexWrap="wrap" mb={2}>
                    {existingImages.map((img, idx) => {
                      const base = (OpenAPI.BASE || "").replace(/\/$/, "")
                      const src = img.startsWith("http")
                        ? img
                        : `${base}/api/v1/uploads/${img}`
                      return (
                        <Box
                          key={img}
                          position="relative"
                          w="100px"
                          h="100px"
                          borderRadius="8px"
                          overflow="hidden"
                          border="1px solid"
                          borderColor={sc.border}
                        >
                          <img
                            src={src}
                            alt={`Tag ${idx + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      )
                    })}
                    {pendingFiles.map((file, idx) => (
                      <Box
                        key={`pending-${idx}`}
                        position="relative"
                        w="100px"
                        h="100px"
                        borderRadius="8px"
                        overflow="hidden"
                        border="1px dashed"
                        borderColor="brand.200"
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`New ${idx + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ))}
                  </HStack>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleImageSelect}
                  />
                  <VisionButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t("tags:form.uploadImage")}
                  </VisionButton>
                </Field>
              </Grid>
            </VisionCard>

            <VisionCard w="100%">
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.assetLocation")}
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.building")}
                  </Text>
                  <VisionInput
                    {...register("location_building")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.floor")}
                  </Text>
                  <VisionInput
                    {...register("location_floor")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.zone")}
                  </Text>
                  <VisionInput
                    {...register("location_zone")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.room")}
                  </Text>
                  <VisionInput
                    {...register("location_room")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
              </Grid>
            </VisionCard>

            <VisionCard w="100%">
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:form.assetManagement")}
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.assignedTo")}
                  </Text>
                  <VisionInput
                    {...register("assigned_to")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.acquisitionDate")}
                  </Text>
                  <VisionInput
                    {...register("acquisition_date")}
                    type="date"
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.assetValue")}
                  </Text>
                  <VisionInput
                    {...register("asset_value", { valueAsNumber: true })}
                    type="number"
                    step="0.01"
                    size="lg"
                    variant="glass"
                  />
                </Field>
                <Field>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.assetStatusField")}
                  </Text>
                  <VisionSelect {...register("asset_status")}>
                    <option value="">{t("common:filter.all")}</option>
                    <option value="in_service">
                      {t("tags:form.statusInService")}
                    </option>
                    <option value="stored">
                      {t("tags:form.statusStored")}
                    </option>
                    <option value="maintenance">
                      {t("tags:form.statusMaintenance")}
                    </option>
                    <option value="retired">
                      {t("tags:form.statusRetired")}
                    </option>
                  </VisionSelect>
                </Field>
                <Field gridColumn={{ md: "span 2" }}>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    fontWeight="500"
                    mb={2}
                  >
                    {t("tags:form.notes")}
                  </Text>
                  <VisionInput
                    {...register("notes")}
                    size="lg"
                    variant="glass"
                  />
                </Field>
              </Grid>
            </VisionCard>

            <HStack gap={3} w="100%">
              <VisionButton
                type="button"
                variant="ghost"
                size="lg"
                onClick={() => navigate({ to: "/tags" })}
              >
                {t("common:actions.cancel")}
              </VisionButton>
              <VisionButton
                type="submit"
                variant="brand"
                size="lg"
                flex="1"
                loading={
                  createTagMutation.isPending || updateTagMutation.isPending
                }
              >
                {createTagMutation.isPending || updateTagMutation.isPending
                  ? isEditMode
                    ? t("common:status.updating")
                    : t("common:status.creating")
                  : isEditMode
                    ? t("tags:form.updateTag")
                    : t("tags:form.createTag")}
              </VisionButton>
            </HStack>
          </VStack>
        </Box>

        {/* ══════ RIGHT — SIDEBAR (25%) ══════ */}
        <VStack gap={6} w="100%" minW={0}>
          {isEditMode && tag && (
            <VisionCard>
              <Heading size="sm" color={sc.heading} mb={4}>
                {t("tags:detail.tagInfo")}
              </Heading>
              <VStack align="stretch" gap={3}>
                <Box>
                  <Text
                    fontSize="xs"
                    color={sc.textMuted}
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {t("tags:form.epcId")}
                  </Text>
                  <Text
                    color={sc.heading}
                    fontSize="sm"
                    mt={1}
                    fontFamily="mono"
                  >
                    {tag.epcid}
                  </Text>
                </Box>
                {tag.tid && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      TID
                    </Text>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      mt={1}
                      fontFamily="mono"
                    >
                      {tag.tid.slice(0, 24)}
                    </Text>
                  </Box>
                )}
                {tag.tag_model && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.tagModel")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {tag.tag_model}
                    </Text>
                  </Box>
                )}
                {tag.template && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.template")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {tag.template.name}
                    </Text>
                  </Box>
                )}
                {tag.state && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.state")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {tag.state}
                    </Text>
                  </Box>
                )}
                {tag.battery_status != null && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.battery")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {(tag.battery_status / 1000).toFixed(2)} V
                    </Text>
                  </Box>
                )}
                {(tag.reading_count ?? 0) > 0 && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.readings")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {(tag.reading_count ?? 0).toLocaleString()}
                    </Text>
                  </Box>
                )}
                {tag.last_seen_by_reader_name && (
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("tags:detail.lastSeenBy")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {tag.last_seen_by_reader_name}
                    </Text>
                  </Box>
                )}
              </VStack>
            </VisionCard>
          )}

          {/* Danger zone — permanent delete bypassing the trashcan. The
              red trash icon + bold-red label is the visual cue that
              this is not the regular soft-delete flow. */}
          {isEditMode && tag && (
            <VisionCard>
              <HStack gap={2} mb={3} align="center">
                <Box color="red.500">
                  <FiTrash2 size={18} />
                </Box>
                <Heading size="sm" color="red.500" fontWeight="bold">
                  {t("tags:form.permanentDelete.title")}
                </Heading>
              </HStack>
              <Text color={sc.textMuted} fontSize="sm" mb={4}>
                {t("tags:form.permanentDelete.help")}
              </Text>
              <VisionButton
                variant="ghost"
                w="100%"
                onClick={() => setPermanentDeleteOpen(true)}
                color="red.500"
                fontWeight="bold"
                borderWidth="1px"
                borderColor="red.500"
                _hover={{ bg: "red.500", color: "white" }}
              >
                <HStack gap={2}>
                  <FiTrash2 />
                  <Text>{t("tags:form.permanentDelete.button")}</Text>
                </HStack>
              </VisionButton>
            </VisionCard>
          )}
        </VStack>
      </Grid>

      {/* Confirmation dialog for permanent delete. Single click on
          "Delete forever" purges the row plus every dependent reading,
          event, and alert — no trash recovery, no undo. */}
      <DialogRoot
        open={permanentDeleteOpen}
        onOpenChange={(e) => setPermanentDeleteOpen(e.open)}
        role="alertdialog"
      >
        <DialogContent>
          <DialogHeader>
            <HStack gap={2} align="center">
              <Box color="red.500">
                <FiTrash2 size={20} />
              </Box>
              <DialogTitle color="red.500" fontWeight="bold">
                {t("tags:form.permanentDelete.confirmTitle")}
              </DialogTitle>
            </HStack>
          </DialogHeader>
          <DialogBody>
            <Text color={sc.text} mb={2}>
              {t("tags:form.permanentDelete.confirmBody", {
                tagName: tag?.name ?? tag?.epcid ?? "",
              })}
            </Text>
            <Text color="red.500" fontWeight="bold">
              {t("tags:form.permanentDelete.confirmWarning")}
            </Text>
          </DialogBody>
          <DialogFooter>
            <HStack gap={3} justify="flex-end" w="100%">
              <VisionButton
                variant="ghost"
                onClick={() => setPermanentDeleteOpen(false)}
                disabled={permanentDeleteMutation.isPending}
              >
                {t("common:actions.cancel")}
              </VisionButton>
              <VisionButton
                onClick={() => permanentDeleteMutation.mutate()}
                loading={permanentDeleteMutation.isPending}
                bg="red.500"
                color="white"
                fontWeight="bold"
                _hover={{ bg: "red.600" }}
              >
                <HStack gap={2}>
                  <FiTrash2 />
                  <Text>
                    {t("tags:form.permanentDelete.confirmButton")}
                  </Text>
                </HStack>
              </VisionButton>
            </HStack>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
